import type {ButtonSize, ButtonVariant} from './Button';
import type {Theme} from '@emotion/react';

interface VariantProps {
    $variant: ButtonVariant;
    theme: Theme;
}

interface SizeProps {
    $size: ButtonSize;
}

/**
 * Returns the background CSS value for the given button variant.
 * Primary uses solid color, primaryGradient uses a 135° linear gradient,
 * accent uses the warm amber, and ghost is transparent.
 *
 * @param props          The styled component props.
 * @param props.$variant The button variant.
 * @param props.theme    The Emotion theme.
 * @returns The CSS background value.
 */
export const getButtonBackground = ({$variant, theme}: VariantProps) => {
    switch ($variant) {
        case 'accent':
            return theme.colors.accentWarm;
        case 'ghost':
            return 'transparent';
        case 'primaryGradient':
            return `linear-gradient(135deg, ${theme.colors.primaryDefault}, ${theme.colors.primaryLight})`;
        case 'primary':
        default:
            return theme.colors.primaryDefault;
    }
};

/**
 * Returns the text color for the given button variant.
 * Ghost uses primaryDefault, accent uses accentDefault (dark amber), all others use white.
 *
 * @param props          The styled component props.
 * @param props.$variant The button variant.
 * @param props.theme    The Emotion theme.
 * @returns The CSS color value.
 */
export const getButtonColor = ({$variant, theme}: VariantProps) => {
    switch ($variant) {
        case 'accent':
            return theme.colors.accentDefault;
        case 'ghost':
            return theme.colors.primaryDefault;
        case 'primary':
        case 'primaryGradient':
        default:
            return theme.colors.white;
    }
};

/**
 * Returns the hover background CSS value for the given button variant.
 * Provides a subtle shift for each variant.
 *
 * @param props          The styled component props.
 * @param props.$variant The button variant.
 * @param props.theme    The Emotion theme.
 * @returns The CSS background value for hover state.
 */
export const getButtonHoverBackground = ({$variant, theme}: VariantProps) => {
    switch ($variant) {
        case 'accent':
            return theme.colors.accentPale;
        case 'ghost':
            return theme.colors.surfaceLow;
        case 'primaryGradient':
            return `linear-gradient(135deg, ${theme.colors.primaryLight}, ${theme.colors.primaryDefault})`;
        case 'primary':
        default:
            return theme.colors.primaryLight;
    }
};

/**
 * Returns the height for the given button size.
 * - large: 4.8rem (Figma default 48px)
 * - medium: 4.0rem
 * - small: 3.6rem (matches chip height).
 *
 * @param props       The styled component props.
 * @param props.$size The button size.
 * @returns The CSS height value.
 */
export const getButtonHeight = ({$size}: SizeProps) => {
    switch ($size) {
        case 'medium':
            return '4rem';
        case 'small':
            return '3.6rem';
        case 'large':
        default:
            return '4.8rem';
    }
};

/**
 * Returns the font-size for the given button size.
 *
 * @param props       The styled component props.
 * @param props.$size The button size.
 * @returns The CSS font-size value.
 */
export const getButtonFontSize = ({$size}: SizeProps) => {
    switch ($size) {
        case 'medium':
            return '1.3rem';
        case 'small':
            return '1.2rem';
        case 'large':
        default:
            return '1.4rem';
    }
};

/**
 * Returns the horizontal padding for the given button size.
 *
 * @param props       The styled component props.
 * @param props.$size The button size.
 * @returns The CSS padding value.
 */
export const getButtonPadding = ({$size}: SizeProps) => {
    switch ($size) {
        case 'medium':
            return '0 2.4rem';
        case 'small':
            return '0 2rem';
        case 'large':
        default:
            return '0 3.2rem';
    }
};

/**
 * Returns the icon size for the given button size.
 *
 * @param props       The styled component props.
 * @param props.$size The button size.
 * @returns The CSS width/height value for the icon container.
 */
export const getButtonIconSize = ({$size}: SizeProps) => {
    switch ($size) {
        case 'medium':
            return '1.6rem';
        case 'small':
            return '1.4rem';
        case 'large':
        default:
            return '1.8rem';
    }
};